import { apiGet, apiPost, apiPut, apiCall } from './apiUtils';
/**
 * Notification Service for Website Builder
 * Handles push notifications and in-app notifications
 */

const notificationService = {
  /**
   * Get all notifications for agent with pagination - matching mobile app
   * @param {number} page - Page number (default: 1)
   * @param {number} limit - Items per page (default: 10)
   */
  getNotifications: async (page = 1, limit = 10) => {
    try {
      console.log(`📱 Fetching Agent Notifications - Page: ${page}, Limit: ${limit}`);

      // Mobile app style pagination parameters
      const params = {
        page: page,
        limit: limit,
        sort: 'created_at',
        order: 'desc'
      };

      // Try multiple possible endpoints for agent notifications
      const endpoints = [
        '/api/notifications/agent',
        '/api/notifications/builder',
        '/api/agent/notifications',
        '/notifications/agent'
      ];

      let response = null;
      let lastError = null;

      for (const endpoint of endpoints) {
        try {
          console.log(`🔍 Trying endpoint: ${endpoint} with params:`, params);
          response = await apiGet(endpoint, params);
          console.log('✅ Notifications Response:', response);

          if (response && (response.success || response.data)) {
            console.log(`✅ Successfully fetched ${response.data?.notifications?.length || response.data?.length || 0} notifications from:`, endpoint);
            return response;
          }
        } catch (err) {
          console.warn(`❌ Endpoint ${endpoint} failed:`, err.message);
          lastError = err;
          continue;
        }
      }

      // If all endpoints failed, return error
      throw lastError || new Error('All notification endpoints failed');
    } catch (error) {
      console.error('Error fetching notifications:', error);
      return { success: false, data: [] };
    }
  },

  /**
   * Mark notification as read
   */
  markAsRead: async (id) => {
    try {
      console.log('🔍 Marking notification as read:', id);

      const endpoints = [
    
        `/api/notifications/builder/${id}/read`,
      ];
// notifications/builder/${id}/read
      for (const endpoint of endpoints) {
        try {
          const response = await apiPut(endpoint);
          console.log('✅ Mark as read response:', response);
          return response;
        } catch (err) {
          console.warn(`❌ Mark as read endpoint ${endpoint} failed:`, err.message);
          continue;
        }
      }

      return { success: false };
    } catch (error) {
      console.error('Error marking notification as read:', error);
      return { success: false };
    }
  },

  /**
   * Mark all notifications as read
   */
  markAllAsRead: async () => {
    try {
      console.log('🔍 Marking all notifications as read');

      const endpoints = [
        // '/api/notifications/agent/read-all',
        '/api/notifications/builder/read-all',
        '/api/agent/notifications/read-all'
      ];

      for (const endpoint of endpoints) {
        try {
          const response = await apiPut(endpoint);
          console.log('✅ Mark all as read response:', response);
          return response;
        } catch (err) {
          console.warn(`❌ Mark all as read endpoint ${endpoint} failed:`, err.message);
          continue;
        }
      }

      return { success: false };
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      return { success: false };
    }
  },

  /**
   * Get unread notification count
   */
  getUnreadCount: async () => {
    try {
      return await apiGet('/api/notifications/builder/unread-count');
    } catch (error) {
      console.error('Error fetching unread count:', error);
      return { success: false, count: 0 };
    }
  },
  /**
   * Get notification reports for admin
   * @param {Object} filters - Optional filters (period, startDate, endDate, type)
   * @returns {Promise<Object>} - Notification reports
   */
  getNotificationReports: async (filters = {}) => {
    console.log('🔍 getNotificationReports called with filters:', filters);

    const params = {};

    // Handle period filter (This Month, Previous Month, This Year)
    if (filters.period) {
      params.period = filters.period;
    }

    // Handle date range filter
    if (filters.dateRange) {
      if (filters.dateRange.from) {
        params.start_date = filters.dateRange.from;
      }
      if (filters.dateRange.to) {
        params.end_date = filters.dateRange.to;
      }
    }

    // Handle report type filter
    if (filters.reportType && filters.reportType.length > 0) {
      params.report_type = filters.reportType.join(',');
    }

    // Handle status filter
    if (filters.status && filters.status !== 'All') {
      params.status = filters.status;
    }

    console.log('🌐 Fetching notification reports with filters:', params);

    return await apiGet('/api/notifications/admin/reports', params);
  },

  /**
   * Send push notification to users
   * @param {Object} notificationData - Notification data
   * @returns {Promise<Object>} - Send result
   */
  sendPushNotification: async (notificationData) => {
    // Use builder endpoint if role is builder, otherwise use admin endpoint
    const endpoint = notificationData?.role === 'builder'
      ? '/api/notifications/builder/push'
      : '/api/notifications/admin/push';

    return await apiPost(endpoint, notificationData);
  },

  /**
   * Get user count based on filters for builders (exact mobile app implementation)
   * Backend filters by: role='agent', fcm_token NOT NULL, status='active'
   * @param {Object} filters - {state, cities[], projectIds[], sendTo: 'all'|'registered'|'non-registered'}
   * @returns {Promise<Object>} - {success: true, count: number, breakdown: {...}}
   */
  getUserCount: async (filters) => {
    try {
      const endpoint = filters?.role === 'admin'
        ? '/api/notifications/admin/user-count'
        : '/api/notifications/builder/user-count';
      const response = await apiPost(endpoint, filters);
      return response;
    } catch (error) {
      const status = error.status || error.response?.status;
      if (status !== 429) {
        console.error('Error getting user count:', error);
      }
      return { success: false, count: 0, breakdown: {} };
    }
  },

  /**
   * Upload notification image
   * @param {File} imageFile - Image file
   * @param {string} role - 'admin' or 'builder'
   * @returns {Promise<Object>} - {success: true, url: string}
   */
  uploadNotificationImage: async (imageFile) => {
    try {
      console.log('[DEBUG] uploadNotificationImage called with:', {
        imageFile: imageFile,
        imageFileType: typeof imageFile,
        imageFileName: imageFile?.fileName,
        imageFileSize: imageFile?.size,
        role: imageFile?.role
      });

      const formData = new FormData();
      // Use the actual file from imageFile.file (web) or imageFile for mobile compatibility
      const actualFile = imageFile.file || imageFile;
      formData.append('image', actualFile);

      // Debug FormData contents
      console.log('[DEBUG] FormData contents:');
      for (let [key, value] of formData.entries()) {
        console.log(`[DEBUG] ${key}:`, value);
        if (value instanceof File) {
          console.log(`[DEBUG] File details - name: ${value.name}, size: ${value.size}, type: ${value.type}`);
        }
      }

      // Use builder endpoint if role is builder, otherwise use admin endpoint (matching mobile app)
      const endpoint = imageFile?.role === 'builder'
        ? `/api/notifications/builder/upload-image`
        : `/api/notifications/admin/upload-image`;

      console.log('[DEBUG] Making API call to:', endpoint);

      const result = await apiCall(endpoint, {
        method: 'POST',
        body: formData
      });

      console.log('[DEBUG] Upload API result:', result);
      return result;
    } catch (error) {
      console.error('Error uploading image:', error);
      return { success: false, message: 'Failed to upload image' };
    }
  },

  /**
   * Get campaigns with filters (for builder reports)
   * @param {Object} filters - Optional filters { fromDate, toDate, status }
   * @returns {Promise<Object>} - List of campaigns
   */
  getCampaigns: async (filters = {}) => {
    const params = {};

    if (filters.fromDate) params.fromDate = filters.fromDate;
    if (filters.toDate) params.toDate = filters.toDate;
    if (filters.status && filters.status !== 'All') params.status = filters.status;

    // Use same endpoint as frontend mobile app
    return await apiGet('/api/notifications/campaigns', params);
  },

  /**
   * Get reports with aggregated stats (mobile app exact endpoint)
   * @param {Object} params - Query parameters { period, start_date, end_date, status }
   * @returns {Promise<Object>} - Reports with aggregated stats
   */
  getReports: async (params = {}) => {
    try {
      console.log('[notificationService] getReports called with params:', params);

      const queryParams = {};

      // Handle period filter (mobile app logic)
      if (params.period) {
        queryParams.period = params.period;
      }

      // Handle custom date range
      if (params.start_date) {
        queryParams.start_date = params.start_date;
      }
      if (params.end_date) {
        queryParams.end_date = params.end_date;
      }

      // Handle status filter
      if (params.status && params.status !== 'All') {
        queryParams.status = params.status;
      }

      console.log('[notificationService] Making API call to /api/notifications/builder/reports with params:', queryParams);

      // Use mobile app's exact reports endpoint
      const response = await apiGet('/api/notifications/builder/reports', queryParams);

      console.log('[notificationService] getReports response:', response);
      return response;
    } catch (error) {
      console.error('[notificationService] getReports error:', error);
      throw error;
    }
  }
};

export default notificationService;